import { normalizeApiError } from '../api/apiError';
import { createReadAsyncThunk } from "../store/createReadAsyncThunk";
import { createSlice } from "@reduxjs/toolkit";
import { Payment } from '../api/endpoints/paymentApi';
import { ApiRequestStatus } from "../enums/apiRequestStatus";
import { PaymentRedirectProps } from "../models/membership/paymentRedirectProps";
import { PaymentDto } from "../models/payment/paymentDto";

interface paymentState {
    paymentRedirectDetails: string | null;
    paymentDetails: PaymentDto | null;
    paymentsByPerson: PaymentDto[] | null;
    heyCentricUrl: string | null;

    paymentRedirectStatus: ApiRequestStatus;
    paymentSummaryStatus: ApiRequestStatus;
    paymentsByPersonStatus: ApiRequestStatus;
    selfServiceStatus: ApiRequestStatus;
}

const initialState: paymentState = {
    paymentRedirectDetails: null,
    paymentDetails: null,
    paymentsByPerson: null,
    heyCentricUrl: null,

    paymentRedirectStatus: ApiRequestStatus.Idle,
    paymentSummaryStatus: ApiRequestStatus.Idle,
    paymentsByPersonStatus: ApiRequestStatus.Idle,
    selfServiceStatus: ApiRequestStatus.Idle
}

export const getPaymentRedirectAsync = createReadAsyncThunk<string, PaymentRedirectProps>(
    'paymentSlice/getPaymentRedirectAsync',
    async (paymentRedirectProps, thunkAPI) => {
        try {
            return await Payment.getPaymentRedirectAsync(paymentRedirectProps);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const getPaymentSummaryAsync = createReadAsyncThunk<PaymentDto, string>(
    'paymentSlice/getPaymentSummaryAsync',
    async (paymentId, thunkAPI) => {
        try {
            return await Payment.getPaymentByPaymentIdAsync(paymentId);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const getPaymentsAsync = createReadAsyncThunk<PaymentDto[]>(
    'paymentSlice/getPaymentsAsync',
    async (_, thunkAPI) => {
        try {
            return await Payment.getPaymentsAsync();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const getSelfServiceAsync = createReadAsyncThunk<string>(
    'paymentSlice/getSelfServiceAsync',
    async (_, thunkAPI) => {
        try {
            return await Payment.getSelfServiceAsync();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const paymentSlice = createSlice({
    name: 'paymentSlice',
    initialState,
    reducers: {

    },
    extraReducers: (builder => {
        builder.addCase(getPaymentRedirectAsync.pending, (state) => {
            state.paymentRedirectStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getPaymentRedirectAsync.fulfilled, (state, action) => {
            state.paymentRedirectDetails = action.payload;
            state.paymentRedirectStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(getPaymentRedirectAsync.rejected, (state) => {
            state.paymentRedirectStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getPaymentSummaryAsync.pending, (state) => {
            state.paymentSummaryStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getPaymentSummaryAsync.fulfilled, (state, action) => {
            state.paymentDetails = action.payload;
            state.paymentSummaryStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(getPaymentSummaryAsync.rejected, (state) => {
            state.paymentSummaryStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getPaymentsAsync.pending, (state) => {
            state.paymentsByPersonStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getPaymentsAsync.fulfilled, (state, action) => {
            state.paymentsByPerson = action.payload;
            state.paymentsByPersonStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(getPaymentsAsync.rejected, (state) => {
            state.paymentsByPersonStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getSelfServiceAsync.pending, (state) => {
            state.selfServiceStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getSelfServiceAsync.fulfilled, (state, action) => {
            state.heyCentricUrl = action.payload;
            state.selfServiceStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(getSelfServiceAsync.rejected, (state) => {
            state.selfServiceStatus = ApiRequestStatus.Rejected;
        });
    })
})
